import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';

// services
import ProjectService from '../../service/project-service';
import ClipService from '../../service/clip-service';

// query client
import { queryClient, queryKeys } from '../../lib/query-client';

// helpers
import displayNetworkError from '../../helpers/displayNetworkError';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';

// styles
import './ModalUploadCueSheetWithClip.css';
import { storeSet } from '../../store';

const ModalUploadCueSheetWithClip = ({
    projectId,
    cueType,
    headerText,
    onClose
}) => {
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        formState: { isValid },
        reset
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            label: '',
            title: '',
            file: null
        }
    });

    const defaultHeaders = {
        MUSIC: __('Carica soundtrack', 'soundbetween'),
        SFX: __('Carica SFX', 'soundbetween'),
        MIX: __('Carica mix', 'soundbetween')
    };

    const header = headerText || defaultHeaders[cueType] || __('Carica clip', 'soundbetween');

    const onSubmit = async (formData) => {
        setLoading(true);

        try {
            // Step 1: Create cuesheet item and get the created item with ID
            const cueSheetItemData = {
                cue_type: cueType,
                label: formData.label,
                title: formData.title,
                timecode: '00:00:00:00'
            };

            const createdItem = await ProjectService.createProjectCueSheetItemAsync(projectId, cueSheetItemData);

            // Step 2: Upload clip with the created cue sheet item ID
            const formDataToSend = new FormData();
            formDataToSend.append('file', formData.file);

            const queryParams = [
                ['projectId', projectId],
                ['clipType', cueType],
                ['cueSheetId', createdItem.id]
            ];

            await ClipService.createClipAsync(formDataToSend, queryParams);

            // Step 3: Invalidate caches and refetch to update UI
            storeSet('refetchCueSheet', projectId);
            // Note: clips are now embedded in cuesheet items, no need to refetch separately 

            handleSuccessAndDisplay(__('Clip è stato caricato con successo', 'soundbetween'));
            reset();
            onClose();
        } catch (error) {
            displayNetworkError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        reset();
        onClose();
    };

    return (
        <ModalDialog
            additionalClasses={['modalUploadCueSheetWithClip']}
            header={header}
            visible={true}
            onSubmit={handleSubmit(onSubmit)}
            onHide={handleCloseModal}>
            <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                <FormField
                    type="text"
                    name="label"
                    label={__('Label', 'soundbetween')}
                    placeholder={__('es 1m3', 'soundbetween')}
                    control={control}
                    rules={{
                        required: __('È obbligatorio', 'soundbetween')
                    }}
                />

                <FormField
                    control={control}
                    name="title"
                    label={__('Titolo', 'soundbetween')}
                    placeholder={__('Inserisci titolo', 'soundbetween')}
                    type="text"
                    rules={{
                        required: __('È obbligatorio', 'soundbetween')
                    }}
                />

                <FormField
                    control={control}
                    name="file"
                    label={__('Carica file', 'soundbetween')}
                    placeholder={__('Scegli un file', 'soundbetween')}
                    type="fileupload"
                    accept=".mp3"
                    rules={{
                        required: __('È obbligatorio', 'soundbetween')
                    }}
                />

                <div className="appFormActions">
                    <ButtonFlat size="lg" type="button" onClick={handleCloseModal}>
                        {__('Anulla', 'soundbetween')}
                    </ButtonFlat>
                    <ButtonFormSubmit disabled={loading || !isValid}>
                        {__('Salva', 'soundbetween')}
                    </ButtonFormSubmit>
                </div>
            </form>
        </ModalDialog>
    );
};

export default ModalUploadCueSheetWithClip;
